import React, { useState, useEffect, useMemo } from 'react';
import { QUESTION_TOPICS, type Question, type QuestionTopic, type QuestionDifficulty } from '../../types';
import { testService, attemptService, questionService } from '../../services/questionBankService';
import type { TestDefinition, TestAttempt } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '@flowcore-water/ui';
import { fuzzyScore, fuzzyMatch } from '../../utils/fuzzySearch';

type ViewMode = 'list' | 'edit' | 'results';

export const TestCreator: React.FC = () => {
  const { user } = useAuth();

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [tests, setTests] = useState<TestDefinition[]>([]);
  const [questionBank, setQuestionBank] = useState<Question[]>([]);
  const [editingTest, setEditingTest] = useState<Partial<TestDefinition> | null>(null);
  const [selectedTestForResults, setSelectedTestForResults] = useState<string | null>(null);

  // Load tests and questions on mount
  useEffect(() => {
    loadTests();
    questionService.getAll().then(setQuestionBank);
  }, []);

  const loadTests = async () => {
    const loaded = await testService.getAll();
    setTests(loaded);
  };

  const handleCreateNew = () => {
    setEditingTest({
      id: `test_${Date.now()}`,
      title: '',
      description: '',
      questionIds: [],
      createdAt: new Date(),
      createdBy: user?.uid || '',
      createdByName: user?.displayName || '',
      timeLimit: null,
    });
    setViewMode('edit');
  };

  const handleEdit = (test: TestDefinition) => {
    setEditingTest({ ...test });
    setViewMode('edit');
  };

  const handleDelete = async (testId: string) => {
    if (confirm('Are you sure you want to delete this test?')) {
      await testService.remove(testId, user!.uid);
      loadTests();
    }
  };

  const handleViewResults = (testId: string) => {
    setSelectedTestForResults(testId);
    setViewMode('results');
  };

  const handleSaveTest = async (test: Partial<TestDefinition>) => {
    if (!test.title?.trim()) {
      alert('Test title is required');
      return;
    }
    if (!test.questionIds || test.questionIds.length === 0) {
      alert('Test must have at least one question');
      return;
    }

    const testDef: TestDefinition = {
      id: test.id || `test_${Date.now()}`,
      title: test.title,
      description: test.description || '',
      questionIds: test.questionIds,
      createdAt: test.createdAt || new Date(),
      createdBy: test.createdBy || user?.uid || '',
      createdByName: test.createdByName || user?.displayName || '',
      timeLimit: test.timeLimit ?? null,
    };

    await testService.save(testDef);
    loadTests();
    setEditingTest(null);
    setViewMode('list');
  };

  return (
    <div style={{ padding: '1.5rem' }}>
      {viewMode === 'list' && (
        <TestListView
          tests={tests}
          onCreateNew={handleCreateNew}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onViewResults={handleViewResults}
        />
      )}

      {viewMode === 'edit' && editingTest && (
        <TestEditorView
          questionBank={questionBank}
          test={editingTest}
          onSave={handleSaveTest}
          onCancel={() => {
            setEditingTest(null);
            setViewMode('list');
          }}
        />
      )}

      {viewMode === 'results' && selectedTestForResults && (
        <TestResultsView
          testId={selectedTestForResults}
          onBack={() => {
            setSelectedTestForResults(null);
            setViewMode('list');
          }}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
// Test List View
// ─────────────────────────────────────────────────────────────────

interface TestListViewProps {
  tests: TestDefinition[];
  onCreateNew: () => void;
  onEdit: (test: TestDefinition) => void;
  onDelete: (testId: string) => void;
  onViewResults: (testId: string) => void;
}

const TestListView: React.FC<TestListViewProps> = ({
  tests,
  onCreateNew,
  onEdit,
  onDelete,
  onViewResults,
}) => {
  const { t } = useTheme();

  return (
    <div>
      <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ color: t.textPrimary, margin: 0, fontSize: '1.75rem' }}>Tests</h1>
        <button
          onClick={onCreateNew}
          style={{
            padding: '0.5rem 1.25rem',
            backgroundColor: t.accent,
            color: '#000',
            border: 'none',
            borderRadius: '0.375rem',
            fontWeight: 600,
            cursor: 'pointer',
            fontSize: '0.875rem',
          }}
        >
          + Create Test
        </button>
      </div>

      {tests.length === 0 ? (
        <div
          style={{
            padding: '2rem',
            textAlign: 'center',
            color: t.textMuted,
            backgroundColor: t.cardBg,
            borderRadius: '0.375rem',
            border: `1px solid ${t.borderSubtle}`,
          }}
        >
          No tests yet. Create one to get started.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '1rem' }}>
          {tests.map((test) => (
            <div
              key={test.id}
              style={{
                padding: '1rem',
                backgroundColor: t.cardBg,
                border: `1px solid ${t.border}`,
                borderRadius: '0.375rem',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                <div style={{ flex: 1 }}>
                  <h2 style={{ color: t.textPrimary, margin: '0 0 0.25rem 0', fontSize: '1.125rem' }}>
                    {test.title}
                  </h2>
                  {test.description && (
                    <p style={{ color: t.textSecondary, margin: '0 0 0.5rem 0', fontSize: '0.875rem' }}>
                      {test.description}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.875rem', color: t.textMuted }}>
                    <span>{test.questionIds.length} questions</span>
                    <span>Created {new Date(test.createdAt).toLocaleDateString()}</span>
                    {test.timeLimit && <span>{test.timeLimit} min limit</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => onEdit(test)}
                    style={{
                      padding: '0.375rem 0.75rem',
                      backgroundColor: t.buttonBg,
                      color: t.buttonText,
                      border: `1px solid ${t.buttonBorder}`,
                      borderRadius: '0.25rem',
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => onViewResults(test.id)}
                    style={{
                      padding: '0.375rem 0.75rem',
                      backgroundColor: t.buttonBg,
                      color: t.buttonText,
                      border: `1px solid ${t.buttonBorder}`,
                      borderRadius: '0.25rem',
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                    }}
                  >
                    Results
                  </button>
                  <button
                    onClick={() => onDelete(test.id)}
                    style={{
                      padding: '0.375rem 0.75rem',
                      backgroundColor: t.failBg,
                      color: t.fail,
                      border: `1px solid ${t.failBorder}`,
                      borderRadius: '0.25rem',
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
// Test Editor View
// ─────────────────────────────────────────────────────────────────

interface TestEditorViewProps {
  questionBank: Question[];
  test: Partial<TestDefinition>;
  onSave: (test: Partial<TestDefinition>) => Promise<void>;
  onCancel: () => void;
}

const TestEditorView: React.FC<TestEditorViewProps> = ({ questionBank, test, onSave, onCancel }) => {
  const { t } = useTheme();
  const [title, setTitle] = useState(test.title || '');
  const [description, setDescription] = useState(test.description || '');
  const [timeLimit, setTimeLimit] = useState(test.timeLimit?.toString() || '');
  const [selectedQuestions, setSelectedQuestions] = useState<string[]>(test.questionIds || []);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave({
        ...test,
        title,
        description,
        questionIds: selectedQuestions,
        timeLimit: timeLimit ? parseInt(timeLimit, 10) : null,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      <button
        onClick={onCancel}
        style={{
          marginBottom: '1rem',
          backgroundColor: 'transparent',
          color: t.accent,
          border: 'none',
          cursor: 'pointer',
          fontSize: '0.875rem',
          textDecoration: 'underline',
        }}
      >
        ← Back
      </button>

      <h1 style={{ color: t.textPrimary, margin: '0 0 1.5rem 0', fontSize: '1.75rem' }}>
        {test.id?.includes('test_') ? 'Create Test' : 'Edit Test'}
      </h1>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem' }}>
        {/* Left: Basic Info */}
        <div>
          <h2 style={{ color: t.textPrimary, fontSize: '1rem', marginBottom: '1rem' }}>Test Details</h2>

          <label style={{ display: 'block', marginBottom: '1rem' }}>
            <div style={{ color: t.textSecondary, fontSize: '0.875rem', marginBottom: '0.25rem' }}>Title *</div>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                backgroundColor: t.inputBg,
                border: `1px solid ${t.inputBorder}`,
                borderRadius: '0.375rem',
                color: t.textPrimary,
                fontSize: '0.875rem',
                boxSizing: 'border-box',
              }}
              placeholder="e.g., Well Construction Basics"
            />
          </label>

          <label style={{ display: 'block', marginBottom: '1rem' }}>
            <div style={{ color: t.textSecondary, fontSize: '0.875rem', marginBottom: '0.25rem' }}>Description</div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                backgroundColor: t.inputBg,
                border: `1px solid ${t.inputBorder}`,
                borderRadius: '0.375rem',
                color: t.textPrimary,
                fontSize: '0.875rem',
                boxSizing: 'border-box',
                minHeight: '6rem',
                fontFamily: 'inherit',
              }}
              placeholder="Optional description..."
            />
          </label>

          <label style={{ display: 'block', marginBottom: '1rem' }}>
            <div style={{ color: t.textSecondary, fontSize: '0.875rem', marginBottom: '0.25rem' }}>Time Limit (minutes)</div>
            <input
              type="number"
              min="1"
              value={timeLimit}
              onChange={(e) => setTimeLimit(e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                backgroundColor: t.inputBg,
                border: `1px solid ${t.inputBorder}`,
                borderRadius: '0.375rem',
                color: t.textPrimary,
                fontSize: '0.875rem',
                boxSizing: 'border-box',
              }}
              placeholder="Leave blank for no limit"
            />
          </label>
        </div>

        {/* Right: Question Selection */}
        <div>
          <h2 style={{ color: t.textPrimary, fontSize: '1rem', marginBottom: '1rem' }}>Select Questions</h2>
          <QuestionSelector
            questionBank={questionBank}
            selectedQuestionIds={selectedQuestions}
            onSelectionChange={setSelectedQuestions}
          />
        </div>
      </div>

      {/* Selected Questions Panel */}
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ color: t.textPrimary, fontSize: '1rem', marginBottom: '1rem' }}>
          Selected Questions ({selectedQuestions.length})
        </h2>
        <SelectedQuestionsPanel
          questionBank={questionBank}
          questionIds={selectedQuestions}
          onRemove={(id) => setSelectedQuestions(selectedQuestions.filter((qid) => qid !== id))}
          onReorder={(from, to) => {
            const newIds = [...selectedQuestions];
            const [item] = newIds.splice(from, 1);
            newIds.splice(to, 0, item);
            setSelectedQuestions(newIds);
          }}
        />
      </div>

      {/* Save/Cancel */}
      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button
          onClick={handleSave}
          disabled={isSaving}
          style={{
            padding: '0.625rem 1.25rem',
            backgroundColor: t.success,
            color: '#000',
            border: 'none',
            borderRadius: '0.375rem',
            fontWeight: 600,
            cursor: 'pointer',
            fontSize: '0.875rem',
            opacity: isSaving ? 0.6 : 1,
          }}
        >
          {isSaving ? 'Saving...' : 'Save Test'}
        </button>
        <button
          onClick={onCancel}
          style={{
            padding: '0.625rem 1.25rem',
            backgroundColor: t.buttonBg,
            color: t.buttonText,
            border: `1px solid ${t.buttonBorder}`,
            borderRadius: '0.375rem',
            fontWeight: 600,
            cursor: 'pointer',
            fontSize: '0.875rem',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
// Question Selector Component
// ─────────────────────────────────────────────────────────────────

interface QuestionSelectorProps {
  questionBank: Question[];
  selectedQuestionIds: string[];
  onSelectionChange: (ids: string[]) => void;
}

const QuestionSelector: React.FC<QuestionSelectorProps> = ({
  questionBank,
  selectedQuestionIds,
  onSelectionChange,
}) => {
  const { t } = useTheme();
  const [mode, setMode] = useState<'random' | 'manual'>('manual');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTopic, setSelectedTopic] = useState<QuestionTopic | ''>('');
  const [selectedDifficulty, setSelectedDifficulty] = useState<QuestionDifficulty | ''>('');
  const [randomCount, setRandomCount] = useState('5');

  // Filter questions for manual search
  const filteredQuestions = useMemo(() => {
    return questionBank
      .filter((q) => {
        if (selectedTopic && q.topic !== selectedTopic) return false;
        if (selectedDifficulty && q.difficulty !== selectedDifficulty) return false;
        if (searchQuery.trim()) {
          const combined = `${q.question} ${q.choices.map((c) => c.text).join(' ')}`;
          if (!fuzzyMatch(searchQuery, combined)) return false;
        }
        return true;
      })
      .sort((a, b) => {
        if (searchQuery.trim()) {
          const scoreA = fuzzyScore(
            searchQuery,
            `${a.question} ${a.choices.map((c) => c.text).join(' ')}`
          );
          const scoreB = fuzzyScore(
            searchQuery,
            `${b.question} ${b.choices.map((c) => c.text).join(' ')}`
          );
          return scoreB - scoreA;
        }
        return 0;
      });
  }, [searchQuery, selectedTopic, selectedDifficulty]);

  const handleGenerateRandom = () => {
    const count = Math.min(parseInt(randomCount, 10) || 5, questionBank.length);
    const shuffled = [...questionBank].sort(() => Math.random() - 0.5);
    const randomIds = shuffled.slice(0, count).map((q) => q.id);
    onSelectionChange(randomIds);
  };

  const handleToggleQuestion = (id: string) => {
    if (selectedQuestionIds.includes(id)) {
      onSelectionChange(selectedQuestionIds.filter((qid) => qid !== id));
    } else {
      onSelectionChange([...selectedQuestionIds, id]);
    }
  };

  return (
    <div style={{ border: `1px solid ${t.border}`, borderRadius: '0.375rem', padding: '1rem' }}>
      {/* Mode Tabs */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', borderBottom: `1px solid ${t.border}`, paddingBottom: '1rem' }}>
        <button
          onClick={() => setMode('random')}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: mode === 'random' ? t.accent : 'transparent',
            color: mode === 'random' ? '#000' : t.textSecondary,
            border: 'none',
            borderRadius: '0.25rem',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: 600,
          }}
        >
          Random Generation
        </button>
        <button
          onClick={() => setMode('manual')}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: mode === 'manual' ? t.accent : 'transparent',
            color: mode === 'manual' ? '#000' : t.textSecondary,
            border: 'none',
            borderRadius: '0.25rem',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: 600,
          }}
        >
          Manual Search
        </button>
      </div>

      {mode === 'random' ? (
        <RandomGenerationForm
          questionBankSize={questionBank.length}
          selectedTopic={selectedTopic}
          setSelectedTopic={setSelectedTopic}
          selectedDifficulty={selectedDifficulty}
          setSelectedDifficulty={setSelectedDifficulty}
          randomCount={randomCount}
          setRandomCount={setRandomCount}
          onGenerate={handleGenerateRandom}
        />
      ) : (
        <ManualSearchForm
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          selectedTopic={selectedTopic}
          setSelectedTopic={setSelectedTopic}
          selectedDifficulty={selectedDifficulty}
          setSelectedDifficulty={setSelectedDifficulty}
          filteredQuestions={filteredQuestions}
          selectedQuestionIds={selectedQuestionIds}
          onToggleQuestion={handleToggleQuestion}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
// Random Generation Form
// ─────────────────────────────────────────────────────────────────

interface RandomGenerationFormProps {
  questionBankSize: number;
  selectedTopic: QuestionTopic | '';
  setSelectedTopic: (topic: QuestionTopic | '') => void;
  selectedDifficulty: QuestionDifficulty | '';
  setSelectedDifficulty: (difficulty: QuestionDifficulty | '') => void;
  randomCount: string;
  setRandomCount: (count: string) => void;
  onGenerate: () => void;
}

const RandomGenerationForm: React.FC<RandomGenerationFormProps> = ({
  questionBankSize,
  selectedTopic,
  setSelectedTopic,
  selectedDifficulty,
  setSelectedDifficulty,
  randomCount,
  setRandomCount,
  onGenerate,
}) => {
  const { t } = useTheme();

  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      <label style={{ display: 'block' }}>
        <div style={{ color: t.textSecondary, fontSize: '0.875rem', marginBottom: '0.25rem' }}>Topic (optional)</div>
        <select
          value={selectedTopic}
          onChange={(e) => setSelectedTopic(e.target.value as QuestionTopic | '')}
          style={{
            width: '100%',
            padding: '0.5rem',
            backgroundColor: t.inputBg,
            border: `1px solid ${t.inputBorder}`,
            borderRadius: '0.375rem',
            color: t.textPrimary,
            fontSize: '0.875rem',
          }}
        >
          <option value="">All Topics</option>
          {QUESTION_TOPICS.map((topic) => (
            <option key={topic.id} value={topic.id}>
              {topic.label}
            </option>
          ))}
        </select>
      </label>

      <label style={{ display: 'block' }}>
        <div style={{ color: t.textSecondary, fontSize: '0.875rem', marginBottom: '0.25rem' }}>Difficulty (optional)</div>
        <select
          value={selectedDifficulty}
          onChange={(e) => setSelectedDifficulty(e.target.value as QuestionDifficulty | '')}
          style={{
            width: '100%',
            padding: '0.5rem',
            backgroundColor: t.inputBg,
            border: `1px solid ${t.inputBorder}`,
            borderRadius: '0.375rem',
            color: t.textPrimary,
            fontSize: '0.875rem',
          }}
        >
          <option value="">All Difficulties</option>
          <option value="basic">Basic</option>
          <option value="intermediate">Intermediate</option>
          <option value="advanced">Advanced</option>
        </select>
      </label>

      <label style={{ display: 'block' }}>
        <div style={{ color: t.textSecondary, fontSize: '0.875rem', marginBottom: '0.25rem' }}>Number of Questions</div>
        <input
          type="number"
          min="1"
          max={questionBankSize}
          value={randomCount}
          onChange={(e) => setRandomCount(e.target.value)}
          style={{
            width: '100%',
            padding: '0.5rem',
            backgroundColor: t.inputBg,
            border: `1px solid ${t.inputBorder}`,
            borderRadius: '0.375rem',
            color: t.textPrimary,
            fontSize: '0.875rem',
            boxSizing: 'border-box',
          }}
        />
      </label>

      <button
        onClick={onGenerate}
        style={{
          padding: '0.625rem 1rem',
          backgroundColor: t.accent,
          color: '#000',
          border: 'none',
          borderRadius: '0.375rem',
          fontWeight: 600,
          cursor: 'pointer',
          fontSize: '0.875rem',
        }}
      >
        Generate Random Questions
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
// Manual Search Form
// ─────────────────────────────────────────────────────────────────

interface ManualSearchFormProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  selectedTopic: QuestionTopic | '';
  setSelectedTopic: (topic: QuestionTopic | '') => void;
  selectedDifficulty: QuestionDifficulty | '';
  setSelectedDifficulty: (difficulty: QuestionDifficulty | '') => void;
  filteredQuestions: Question[];
  selectedQuestionIds: string[];
  onToggleQuestion: (id: string) => void;
}

const ManualSearchForm: React.FC<ManualSearchFormProps> = ({
  searchQuery,
  setSearchQuery,
  selectedTopic,
  setSelectedTopic,
  selectedDifficulty,
  setSelectedDifficulty,
  filteredQuestions,
  selectedQuestionIds,
  onToggleQuestion,
}) => {
  const { t } = useTheme();

  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      <input
        type="text"
        placeholder="Search questions by text or answer choices..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        style={{
          width: '100%',
          padding: '0.625rem',
          backgroundColor: t.inputBg,
          border: `1px solid ${t.inputBorder}`,
          borderRadius: '0.375rem',
          color: t.textPrimary,
          fontSize: '0.875rem',
          boxSizing: 'border-box',
        }}
      />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <select
          value={selectedTopic}
          onChange={(e) => setSelectedTopic(e.target.value as QuestionTopic | '')}
          style={{
            padding: '0.5rem',
            backgroundColor: t.inputBg,
            border: `1px solid ${t.inputBorder}`,
            borderRadius: '0.375rem',
            color: t.textPrimary,
            fontSize: '0.875rem',
          }}
        >
          <option value="">All Topics</option>
          {QUESTION_TOPICS.map((topic) => (
            <option key={topic.id} value={topic.id}>
              {topic.label}
            </option>
          ))}
        </select>

        <select
          value={selectedDifficulty}
          onChange={(e) => setSelectedDifficulty(e.target.value as QuestionDifficulty | '')}
          style={{
            padding: '0.5rem',
            backgroundColor: t.inputBg,
            border: `1px solid ${t.inputBorder}`,
            borderRadius: '0.375rem',
            color: t.textPrimary,
            fontSize: '0.875rem',
          }}
        >
          <option value="">All Difficulties</option>
          <option value="basic">Basic</option>
          <option value="intermediate">Intermediate</option>
          <option value="advanced">Advanced</option>
        </select>
      </div>

      <div
        style={{
          maxHeight: '400px',
          overflowY: 'auto',
          border: `1px solid ${t.borderSubtle}`,
          borderRadius: '0.375rem',
          padding: '0.5rem',
        }}
      >
        {filteredQuestions.length === 0 ? (
          <div style={{ padding: '1rem', color: t.textMuted, textAlign: 'center', fontSize: '0.875rem' }}>
            No questions match your search
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '0.5rem' }}>
            {filteredQuestions.map((question) => (
              <label
                key={question.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  padding: '0.75rem',
                  backgroundColor: selectedQuestionIds.includes(question.id) ? t.accentBg : 'transparent',
                  borderRadius: '0.25rem',
                  cursor: 'pointer',
                  border: selectedQuestionIds.includes(question.id)
                    ? `1px solid ${t.accent}`
                    : `1px solid transparent`,
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedQuestionIds.includes(question.id)}
                  onChange={() => onToggleQuestion(question.id)}
                  style={{ marginRight: '0.75rem', marginTop: '0.125rem', cursor: 'pointer' }}
                />
                <div style={{ flex: 1, fontSize: '0.8rem' }}>
                  <div style={{ color: t.textPrimary, marginBottom: '0.25rem', fontWeight: 500 }}>
                    {question.question}
                  </div>
                  <div style={{ color: t.textMuted, fontSize: '0.75rem' }}>
                    {question.topic} • {question.difficulty}
                  </div>
                </div>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
// Selected Questions Panel
// ─────────────────────────────────────────────────────────────────

interface SelectedQuestionsPanelProps {
  questionBank: Question[];
  questionIds: string[];
  onRemove: (id: string) => void;
  onReorder: (from: number, to: number) => void;
}

const SelectedQuestionsPanel: React.FC<SelectedQuestionsPanelProps> = ({
  questionBank,
  questionIds,
  onRemove,
  onReorder,
}) => {
  const { t } = useTheme();

  if (questionIds.length === 0) {
    return (
      <div
        style={{
          padding: '2rem',
          textAlign: 'center',
          color: t.textMuted,
          backgroundColor: t.cardBg,
          borderRadius: '0.375rem',
          border: `1px solid ${t.borderSubtle}`,
        }}
      >
        No questions selected yet
      </div>
    );
  }

  return (
    <div
      style={{
        border: `1px solid ${t.border}`,
        borderRadius: '0.375rem',
        overflow: 'hidden',
      }}
    >
      {questionIds.map((qid, idx) => {
        const question = questionBank.find((q) => q.id === qid);
        if (!question) return null;

        return (
          <div
            key={qid}
            style={{
              padding: '1rem',
              borderBottom: idx < questionIds.length - 1 ? `1px solid ${t.borderSubtle}` : 'none',
              backgroundColor: idx % 2 === 0 ? t.cardBg : 'transparent',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, color: t.textPrimary, marginBottom: '0.25rem', fontSize: '0.9rem' }}>
                {idx + 1}. {question.question}
              </div>
              <div style={{ color: t.textMuted, fontSize: '0.8rem' }}>
                {question.topic} • {question.difficulty}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '1rem', flexShrink: 0 }}>
              <button
                onClick={() => idx > 0 && onReorder(idx, idx - 1)}
                disabled={idx === 0}
                style={{
                  padding: '0.375rem 0.5rem',
                  backgroundColor: t.buttonBg,
                  color: t.buttonText,
                  border: `1px solid ${t.buttonBorder}`,
                  borderRadius: '0.25rem',
                  cursor: idx === 0 ? 'default' : 'pointer',
                  fontSize: '0.7rem',
                  opacity: idx === 0 ? 0.4 : 1,
                }}
              >
                ↑
              </button>
              <button
                onClick={() => idx < questionIds.length - 1 && onReorder(idx, idx + 1)}
                disabled={idx === questionIds.length - 1}
                style={{
                  padding: '0.375rem 0.5rem',
                  backgroundColor: t.buttonBg,
                  color: t.buttonText,
                  border: `1px solid ${t.buttonBorder}`,
                  borderRadius: '0.25rem',
                  cursor: idx === questionIds.length - 1 ? 'default' : 'pointer',
                  fontSize: '0.7rem',
                  opacity: idx === questionIds.length - 1 ? 0.4 : 1,
                }}
              >
                ↓
              </button>
              <button
                onClick={() => onRemove(qid)}
                style={{
                  padding: '0.375rem 0.5rem',
                  backgroundColor: t.failBg,
                  color: t.fail,
                  border: `1px solid ${t.failBorder}`,
                  borderRadius: '0.25rem',
                  cursor: 'pointer',
                  fontSize: '0.7rem',
                }}
              >
                Remove
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
// Test Results View
// ─────────────────────────────────────────────────────────────────

interface TestResultsViewProps {
  testId: string;
  onBack: () => void;
}

const TestResultsView: React.FC<TestResultsViewProps> = ({ testId, onBack }) => {
  const { t } = useTheme();
  const [attempts, setAttempts] = useState<TestAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [test, setTest] = useState<TestDefinition | null>(null);

  useEffect(() => {
    loadData();
  }, [testId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const allTests = await testService.getAll();
      const foundTest = allTests.find((t) => t.id === testId);
      setTest(foundTest || null);

      const allAttempts = await attemptService.getAll();
      const testAttempts = allAttempts.filter((a) => a.testId === testId);
      setAttempts(testAttempts);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '1.5rem', color: t.textMuted }}>
        Loading results...
      </div>
    );
  }

  const avgScore = attempts.length > 0 ? Math.round(attempts.reduce((sum, a) => sum + a.score, 0) / attempts.length) : 0;

  return (
    <div>
      <button
        onClick={onBack}
        style={{
          marginBottom: '1rem',
          backgroundColor: 'transparent',
          color: t.accent,
          border: 'none',
          cursor: 'pointer',
          fontSize: '0.875rem',
          textDecoration: 'underline',
        }}
      >
        ← Back
      </button>

      <h1 style={{ color: t.textPrimary, margin: '0 0 1rem 0', fontSize: '1.75rem' }}>
        {test?.title || 'Test'} - Results
      </h1>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
        <div
          style={{
            padding: '1rem',
            backgroundColor: t.cardBg,
            border: `1px solid ${t.border}`,
            borderRadius: '0.375rem',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '0.875rem', color: t.textMuted, marginBottom: '0.5rem' }}>Total Attempts</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: t.accent }}>
            {attempts.length}
          </div>
        </div>

        <div
          style={{
            padding: '1rem',
            backgroundColor: t.cardBg,
            border: `1px solid ${t.border}`,
            borderRadius: '0.375rem',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '0.875rem', color: t.textMuted, marginBottom: '0.5rem' }}>Average Score</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: t.success }}>
            {avgScore}%
          </div>
        </div>

        <div
          style={{
            padding: '1rem',
            backgroundColor: t.cardBg,
            border: `1px solid ${t.border}`,
            borderRadius: '0.375rem',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '0.875rem', color: t.textMuted, marginBottom: '0.5rem' }}>Pass Rate</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: t.accent }}>
            {attempts.length > 0
              ? Math.round(
                  (attempts.filter((a) => a.score >= 70).length / attempts.length) * 100
                )
              : 0}
            %
          </div>
        </div>
      </div>

      <h2 style={{ color: t.textPrimary, fontSize: '1.125rem', marginBottom: '1rem' }}>Attempts</h2>

      {attempts.length === 0 ? (
        <div
          style={{
            padding: '2rem',
            textAlign: 'center',
            color: t.textMuted,
            backgroundColor: t.cardBg,
            borderRadius: '0.375rem',
            border: `1px solid ${t.borderSubtle}`,
          }}
        >
          No attempts yet
        </div>
      ) : (
        <div
          style={{
            border: `1px solid ${t.border}`,
            borderRadius: '0.375rem',
            overflow: 'hidden',
          }}
        >
          {attempts.map((attempt, idx) => (
            <div
              key={attempt.id}
              style={{
                padding: '1rem',
                borderBottom: idx < attempts.length - 1 ? `1px solid ${t.borderSubtle}` : 'none',
                backgroundColor: idx % 2 === 0 ? t.cardBg : 'transparent',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <div style={{ fontWeight: 600, color: t.textPrimary, marginBottom: '0.25rem' }}>
                  {attempt.userName}
                </div>
                <div style={{ fontSize: '0.875rem', color: t.textMuted }}>
                  {new Date(attempt.completedAt).toLocaleString()}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div
                  style={{
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    color: attempt.score >= 70 ? t.success : t.fail,
                  }}
                >
                  {attempt.score}%
                </div>
                <div style={{ fontSize: '0.875rem', color: t.textMuted }}>
                  {attempt.correctCount}/{attempt.totalCount}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
